import * as React from 'react'
import { useEffect } from 'react'

import { Alert, Box, Button, Text } from '@factorialco/deprecated-design-system'
import { Form } from 'factorial-form'
import xor from 'lodash/xor'

import { observer } from 'mobx-react'

import FormRow from 'modules/core/components/FormRow'
import DeprecatedText from 'modules/core/components/Inputs/Text'
import Modal from 'modules/core/components/Modal'
import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'
import legalEntities from 'modules/core/store/collections/legalEntities'

import LegalEntitySelector from 'modules/settings/components/LegalEntitySelector'
import legalGenders from 'modules/settings/store/collections/legalGenders'
import type Gender from 'modules/settings/store/models/Gender'
import type LegalGender from 'modules/settings/store/models/LegalGender'

class SchemaForm extends Form {
  constructor(values: object) {
    super(values, {
      name: 'string',
    })
  }
}

interface Props {
  gender: Gender
  legalGendersSelected: LegalGender[]
  onClose: () => void
}

const EditModal = ({ gender, legalGendersSelected, onClose }: Props) => {
  const form = React.useRef(new SchemaForm(gender?.toJS())).current

  const [selectedIds, setSelectedIds] = React.useState<string[]>([])

  const onSubmit = async (closePortal: ClosePortalFn) => {
    const { success } = await asyncAction(async () => {
      gender.save({
        name: form.get('name').value,
      })
    })

    const legalGenderPreviousIds = legalGendersSelected.map(
      (item) => item.attributes.legal_entity_id as string
    )
    const legalGenderToAddIds = selectedIds.filter((id) => !legalGenderPreviousIds.includes(id))
    const legalGenderToDeleteIds = legalGenderPreviousIds.filter((id) => !selectedIds.includes(id))

    await Promise.all([
      legalGenderToAddIds.map((legalEntityId) => {
        legalGenders.create({
          gender_id: gender.id,
          legal_entity_id: legalEntityId,
        })
      }),
      legalGenderToDeleteIds.map((legalEntityId) => {
        const legalGender = legalGenders.find(
          (legalGender) =>
            legalGender.attributes.gender_id === gender.id &&
            legalGender.attributes.legal_entity_id === legalEntityId
        )
        legalGender?.destroy()
      }),
    ])

    if (success) closePortal()
  }

  const onSelectMultiple = (legalEntityIds: string[]) => {
    setSelectedIds(legalEntityIds)
  }

  useEffect(() => {
    onSelectMultiple(legalGendersSelected.map((item) => item.attributes.legal_entity_id as string))
  }, [])

  const onSelect = (legalEntityId: string | null | undefined) => {
    if (!legalEntityId) return setSelectedIds([])

    setSelectedIds(xor(selectedIds, [legalEntityId]))
  }

  const disabled = !form.get('name').value || selectedIds?.length === 0

  return (
    <Modal
      illustration="input-field"
      title={i18n.t('gender_settings.update.title')}
      description={i18n.t('gender_settings.update.description')}
      onClose={onClose}
      onSubmit={onSubmit}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('gender_settings.update.cancel')}
          </Button>
          <Button disabled={disabled} submit>
            {i18n.t('gender_settings.update.accept')}
          </Button>
        </>
      }
    >
      <FormRow>
        <DeprecatedText
          label={i18n.t('gender_settings.edit.name_field')}
          field={form.get('name')}
        />
      </FormRow>
      <FormRow>
        <Box gap="s4">
          <Box>
            <Text.Normal size="200" weight="medium" color="grey1000">
              {i18n.t('gender_settings.edit.legal_entities_field')}
            </Text.Normal>
          </Box>
          <LegalEntitySelector
            onSelect={onSelect}
            onSelectMultiple={onSelectMultiple}
            selectedIds={selectedIds}
            legalEntities={legalEntities.models}
          />
        </Box>
      </FormRow>
      <Alert color="warning">{i18n.t('gender_settings.edit.alert_remove_legal_gender')}</Alert>
    </Modal>
  )
}

export default observer(EditModal)
